import React, { useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';
import { FormErrors, FormValues, initialValues } from '../../types/ChampionApplicationFormTypes';
import { validateChampionApplication } from '../../utils/ChampionApplicationFormValidation';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { AddressSection } from 'components/champions/sections/AddressSection';
import { WorkPartnerSection } from 'components/champions/sections/WorkPartnerSection';
import { ProductsSection } from 'components/champions/sections/ProductsSection';
import { MotivationSection } from 'components/champions/sections/MotivationSection';
import { PreferenceSection } from 'components/champions/sections/PreferenceSection';
import { PreferenceDetailsSection } from 'components/champions/sections/PreferenceDetailsSection';
import { HowHeardSection } from 'components/champions/sections/HowHeardSection';
import { AgreementsSection } from 'components/champions/sections/AgreementsSection';

const ChampionApplicationForm: React.FC = () => {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateMultiSelect = (key: keyof FormValues, optionId: string) => {
    setValues((prev) => {
      const current = prev[key] as string[];
      const exists = current.includes(optionId);
      const next = exists ? current.filter((id) => id !== optionId) : [...current, optionId];
      return { ...prev, [key]: next as any };
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateChampionApplication(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      // TODO: Replace with submitChampionSignup from @atlassian/community-library
      // once it is merged and OAuth has been set up for Salesforce in upp-service-api.
      console.log('Submitting Champions application payload:', values);
      setValues(initialValues);
      setErrors({});
    } catch (err) {
      console.error('Submit error', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div css={outerStyles}>
      <div css={innerStyles}>
        <form css={formStyles} onSubmit={handleSubmit}>
          <h1 css={headingLargeStyles}>Atlassian Champions</h1>

          <h2 css={headingMediumStyles}>Apply to be an Atlassian Champion</h2>

          <p css={marginBottomXSmallStyles}>
            Questions marked with an asterisk<abbr css={requiredStyles} title="required">*</abbr> are required.
          </p>
          <p css={marginBottomLargeStyles}>
            Issues with this form? We&rsquo;re here to help. Email us at{' '}
            <a href="mailto:communitymanagers@atlassian.com">communitymanagers@atlassian.com</a>
          </p>

          <h3 css={headingMediumStyles}>Before you begin</h3>
          <p css={marginBottomLargeStyles}>
            We&rsquo;re excited for you to start your champion journey. You&rsquo;ll need to meet some requirements to
            join the program. See{' '}
            <a
              href="https://community.atlassian.com/champions#whos-eligible-to-apply"
              target="_blank"
              rel="noopener noreferrer"
            >
              Who&rsquo;s eligible to apply?
            </a>
          </p>

          <PersonalInfoSection
            values={values}
            updateField={updateField}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName,
              email: errors.email,
              communityProfileUrl: errors.communityProfileUrl,
              linkedinUrl: errors.linkedinUrl,
            }}
          />

          <AddressSection
            values={values}
            updateField={updateField}
            errors={{
              street: errors.street,
              city: errors.city,
              stateProvince: errors.stateProvince,
              postalCode: errors.postalCode,
              country: errors.country,
            }}
          />

          <WorkPartnerSection values={values} updateField={updateField} errors={{ partnerType: errors.partnerType }} />

          <ProductsSection
            values={values}
            updateMultiSelect={updateMultiSelect}
            updateField={updateField}
            errors={{ products: errors.products, otherAtlassianProducts: errors.otherAtlassianProducts }}
          />

          <MotivationSection
            values={values}
            updateField={updateField}
            errors={{ whyBecomeChampion: errors.whyBecomeChampion }}
          />

          <PreferenceSection
            values={values}
            updateField={updateField}
            errors={{ championPreference: errors.championPreference }}
          />

          <PreferenceDetailsSection
            values={values}
            updateField={updateField}
            errors={{
              howContributeOnline: errors.howContributeOnline,
              attendedAce: errors.attendedAce,
              fourEventsPerYear: errors.fourEventsPerYear,
              activityAndEvent: errors.activityAndEvent,
              whyToCreateContent: errors.whyToCreateContent,
              contentSharingFrequency: errors.contentSharingFrequency,
              bestContent1: errors.bestContent1,
              bestContent2: errors.bestContent2,
              bestContent3: errors.bestContent3,
              primarySocialProfile: errors.primarySocialProfile,
              commitSharingContent: errors.commitSharingContent,
              trainingFrequency: errors.trainingFrequency,
              trainingSources: errors.trainingSources,
              activeCug: errors.activeCug,
              eventsPerYear: errors.eventsPerYear,
              discussInCoE: errors.discussInCoE,
              coeFocus: errors.coeFocus,
            }}
          />

          <HowHeardSection
            values={values}
            updateField={updateField}
            errors={{ howHeard: errors.howHeard, howHeardOther: errors.howHeardOther }}
          />

          <AgreementsSection
            values={values}
            updateField={updateField}
            errors={{ agreePrivacy: errors.agreePrivacy, agreeNda: errors.agreeNda }}
          />

          <div css={marginTopLargeStyles}>
            <Button
              type="submit"
              appearance="primary"
              isDisabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting\u2026' : 'Submit application'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChampionApplicationForm;

const outerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  padding: token('space.600'),
});

const innerStyles = css({
  width: '100%',
  maxWidth: '800px',
});

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.0'),
});

const headingLargeStyles = css({
  fontSize: '28px',
  fontWeight: 600,
  marginBottom: token('space.200'),
});

const headingMediumStyles = css({
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: token('space.200'),
});

const marginBottomXSmallStyles = css({
  marginBottom: token('space.100'),
});

const marginBottomLargeStyles = css({
  marginBottom: token('space.400'),
});

const marginTopLargeStyles = css({
  marginTop: token('space.400'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
  textDecoration: 'none',
});
